import { useCallback, useMemo, useRef, useState } from 'react';

import { t } from '@grafana/i18n';
import { Drawer } from '@grafana/ui';
import { QueryLibraryContext, useQueryLibraryContext } from 'app/features/explore/QueryLibrary/QueryLibraryContext';
import { type SavedQuery } from 'app/features/explore/QueryLibrary/types';

import { QueryLibraryInteractions } from '../../QueryLibraryAnalyticsEvents';
import { SavedQueriesDetailsPanel, type SavedQuerySaveAction } from '../SavedQueriesDetailsPanel';

type Props = {
  query: SavedQuery | undefined;
  onClose: () => void;
  /** Called with the deleted query's uid so the page can prune it from its bulk selection. */
  onDeleted?: (uid: string) => void;
  /** Called with the newly-created query's uid after saving a new/duplicated query. */
  onSaveNew?: (uid: string) => void;
  /**
   * When provided, the panel footer renders a split Save button (see SavedQueriesDetailsPanel).
   */
  saveActions?: SavedQuerySaveAction[];
  /**
   * Where the Explore editing banner should return to after Save / Discard. Supplied by the owning page,
   * which owns the `?editQuery` contract this drawer opens from (list: `?editQuery=<uid>`; detail:
   * `/saved-queries/:uid?editQuery=true`). Omit when the query has no uid yet.
   */
  savedQueryReturnTo?: string;
  mode?: 'edit' | 'duplicate' | 'new';
};

export function EditQueryDrawer({
  query,
  onClose,
  onDeleted,
  onSaveNew,
  saveActions,
  savedQueryReturnTo,
  mode,
}: Props) {
  const parent = useQueryLibraryContext();
  const { triggerAnalyticsEvent } = parent;
  const [isEditing, setIsEditing] = useState(!!query);
  const [overrides, setOverrides] = useState<Record<string, string>>({});

  const drawerMode = mode ?? (query?.uid ? 'edit' : 'new');
  const isDuplicate = drawerMode === 'duplicate';

  const closeGuardRef = useRef<() => boolean>(() => true);
  const guardBlockedRef = useRef(false);

  const guardedClose = useCallback(
    (_isSelectingQuery?: boolean, closedToEditInExplore?: boolean): boolean => {
      if (!closeGuardRef.current()) {
        // Guard opened the discard-changes confirmation modal; abort the close so any pending
        // navigation (Edit in Explore) can bail out too.
        guardBlockedRef.current = true;
        return false;
      }

      if (closedToEditInExplore) {
        // Not abandonment: the user is continuing the edit in Explore. Mirrors the modal, which emits
        // this same event from the provider's closeDrawer.
        triggerAnalyticsEvent(QueryLibraryInteractions.queryLibraryClosedToEditQueryInExplore);
      } else {
        // A successful save closes the drawer through onSave (straight to onClose), bypassing this
        // function — so reaching here always means the query was not saved.
        triggerAnalyticsEvent(
          isDuplicate
            ? QueryLibraryInteractions.duplicateDrawerClosedWithoutSaving
            : QueryLibraryInteractions.editDrawerClosedWithoutSaving,
          isDuplicate
            ? { hadUnsavedChanges: guardBlockedRef.current }
            : { hadUnsavedChanges: guardBlockedRef.current, isNew: drawerMode === 'new' }
        );
      }

      onClose();
      return true;
    },
    [onClose, drawerMode, isDuplicate, triggerAnalyticsEvent]
  );

  // The drawer inherits the render context (and its analytics stamping) from the parent provider
  const value = useMemo(
    () => ({
      ...parent,
      closeDrawer: guardedClose,
      onSave: onClose,
      isEditingQuery: isEditing,
      setIsEditingQuery: setIsEditing,
      templateVariableOverrides: overrides,
      setTemplateVariableOverrides: setOverrides,
      setCloseGuard: (shouldAllowClose: () => boolean) => {
        closeGuardRef.current = shouldAllowClose;
      },
      clearCloseGuard: () => {
        closeGuardRef.current = () => true;
      },
      newQuery: undefined,
      openedToSaveQuery: false,
    }),
    [parent, isEditing, overrides, onClose, guardedClose]
  );

  if (!query) {
    return null;
  }

  const title = isDuplicate
    ? t('saved-queries.page.edit-drawer.duplicate-title', 'Duplicate query')
    : drawerMode === 'edit'
      ? t('saved-queries.page.edit-drawer.title', 'Edit query')
      : t('saved-queries.page.edit-drawer.new-title', 'New query');

  return (
    <Drawer title={title} onClose={guardedClose}>
      <QueryLibraryContext.Provider value={value}>
        <SavedQueriesDetailsPanel
          query={query}
          onSaveNew={onSaveNew}
          saveActions={saveActions}
          standalone
          isDuplicate={isDuplicate}
          savedQueryReturnTo={savedQueryReturnTo}
          onDelete={() => {
            // Prune the deleted query from the page's bulk selection before closing, matching the
            // bulk/row-menu delete paths — otherwise the page stays in bulk mode with a stale uid.
            if (query.uid) {
              onDeleted?.(query.uid);
            }
            onClose();
          }}
        />
      </QueryLibraryContext.Provider>
    </Drawer>
  );
}
